import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import type { StyleChildProps } from 'hb-builder/components/BuilderComponents/block/Strips/types/props';
import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import Padding from 'hb-builder/components/common/SettingsLibrary/CommonSettings/Padding';
import SelectSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/SelectSetting';
import RadiosInput from 'hb-builder/components/common/SettingsLibrary/ComposedInputs/RadiosInput';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import { isPaddingTooBig } from 'hb-builder/utils/attributes';

import { getColorModeOptions } from '../utils/constants';

const GlobalSettings = ({
  frames,
  handleBulkChange,
  item,
  onChange,
  permissions: { can: { alertOnCustomPadding } } = {
    can: {},
  },
}: Omit<StyleChildProps, 'parentItem'>) => {
  const {
    features: { color },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();
  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };
  const stripsTemplateSlider = item.children?.[0];
  const [firstStripItem] = stripsTemplateSlider?.children || [];

  // RadioInput option values must be unique on a page,
  // so we added the name of the item as an identifier
  const optionPrefix = 'global-';

  const isFramesColorUnique =
    item.attributes?.backgroundColorMode !== 'different';

  if (!stripsTemplateSlider) {
    return null;
  }

  return (
    <>
      <Box px={5} width="100%">
        <SelectSetting
          fallbackValue="4"
          item={stripsTemplateSlider}
          onChange={onChange}
          options={[
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
          ]}
          settingKey="numberOfItems"
          title={s__('Builder|Amount of cards to display per page')}
        />
      </Box>
      <Divider m={0} mb={4} />
      <Box px={5}>
        <Padding
          alert={{
            enabled: alertOnCustomPadding,
            fillConditions: (padding) => isPaddingTooBig(padding, 30),
            message: s__(
              'Builder|Please check if this value displays well on mobile',
            ),
          }}
          item={item}
          onChange={onChange}
          settingKey="containerPadding"
          title={__('Padding (in pixels)')}
        />
      </Box>
      <Divider m={0} mb={4} />
      <Box flexDirection="column" px={5}>
        <ColorSetting
          item={item}
          onChange={onChange}
          settingKey="containerBackgroundColor"
          title={__('Section background color')}
          transparent
          {...colorSettingsProps}
        />
        <CheckboxSetting
          settingKey="fullWidth"
          title={__('Full width background')}
          item={item}
          onChange={onChange}
        />
      </Box>
      <Divider m={0} mb={4} />
      <Box flexDirection="column" px={5} mb={3}>
        <InputTitle
          boxProps={{ mb: 3 }}
          id="settingsGlobalStripsColorMode"
          label={__('Frame background color')}
        />
        <RadiosInput
          direction="horizontal"
          options={getColorModeOptions({ optionPrefix })}
          name={`${optionPrefix}color-mode"`}
          onChange={(value) => {
            const finalValue = value.replace(optionPrefix, '');

            onChange({
              id: item.id,
              settingKey: 'attributes.backgroundColorMode',
              value: finalValue,
            });
          }}
          value={`${optionPrefix}${
            item.attributes?.backgroundColorMode || 'same'
          }`}
        />
      </Box>
      {isFramesColorUnique ? (
        <Box px={5}>
          <ColorSetting
            item={firstStripItem}
            onChange={({ settingKey, value }) =>
              handleBulkChange({ element: 'frame', settingKey, value })
            }
            settingKey="frameBackgroundColor"
            title={__('Background color')}
            transparent
            {...colorSettingsProps}
          />
        </Box>
      ) : (
        <Box flexDirection="column" px={5}>
          {frames?.map((frame, index) => (
            <ColorSetting
              defaultAttributes={{
                frameBackgroundColor:
                  firstStripItem.attributes?.frameBackgroundColor,
              }}
              key={frame.id}
              item={frame}
              onChange={onChange}
              settingKey="frameBackgroundColor"
              title={s__('Builder|Frame %{number}').i__({
                number: (index + 1).toString(),
              })}
              transparent
              {...colorSettingsProps}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default GlobalSettings;
